import { createContext, useContext } from 'react';
import type { View } from 'react-native';
import { useStore, type StoreApi } from 'zustand';

import type { WizardStepKey } from '../../model/step';
import type { ReplyDraftTourSegment } from '../../model/tour';
import {
  createReplyDraftTourStore,
  type ReplyDraftTourStore,
} from '../../state/replyDraftTourStore';

export type TourAdvanceInput = {
  segment: ReplyDraftTourSegment;
  zone: number;
  isLastStep: boolean;
  handleNext: () => void;
  handleStop: () => void;
};

/**
 * What the wizard's surfaces can ask of the tour. Every function keeps one
 * identity for the life of the wizard, so passing these around never
 * re-renders a consumer.
 */
export type ReplyDraftTourActions = {
  /** Starts the first-draft tour, if the callout still allows it. */
  start: (step: WizardStepKey) => void;
  replay: (step: WizardStepKey) => void;
  advance: (input: TourAdvanceInput) => void;
  back: (segment: ReplyDraftTourSegment, zone: number, handlePrev: () => void) => void;
  skip: (segment: ReplyDraftTourSegment, zone: number, handleStop: () => void) => void;
  /** Records the view a zone wraps, so the tour can scroll it into sight. Held outside the store. */
  setZoneNode: (key: string, node: View | null) => void;
  /** Scrolls a zone into sight before the tour measures it. */
  reveal: (segment: ReplyDraftTourSegment, zone: number) => Promise<void>;
  /** The driver put a segment on screen. */
  segmentShown: (segment: ReplyDraftTourSegment) => void;
};

type ReplyDraftTourContextValue = {
  store: StoreApi<ReplyDraftTourStore>;
  actions: ReplyDraftTourActions;
};

const noop = () => {};

const IDLE_ACTIONS: ReplyDraftTourActions = {
  start: noop,
  replay: noop,
  advance: noop,
  back: noop,
  skip: noop,
  setZoneNode: noop,
  reveal: async () => {},
  segmentShown: noop,
};

/**
 * Stands in outside the tour's provider, where no tour runs, so a step can
 * render its zones on its own, as its tests do.
 */
const IDLE_STORE = createReplyDraftTourStore();

export const ReplyDraftTourContext = createContext<ReplyDraftTourContextValue | null>(null);

export function useReplyDraftTourActions(): ReplyDraftTourActions {
  return useContext(ReplyDraftTourContext)?.actions ?? IDLE_ACTIONS;
}

export function useReplyDraftTourStoreApi(): StoreApi<ReplyDraftTourStore> {
  return useContext(ReplyDraftTourContext)?.store ?? IDLE_STORE;
}

/** Subscribes to one slice of the tour's state. Always pass a narrow selector. */
export function useReplyDraftTourStore<Selected>(
  selector: (state: ReplyDraftTourStore) => Selected
): Selected {
  return useStore(useReplyDraftTourStoreApi(), selector);
}
